import { ChevronDown, Zap } from "lucide-react";

import { LeadTile, ProTradeTease, ViewAllButton } from "@/components/trade/LeadTile";
import { FREE_COMPARE_LEADS, type PanelRow } from "@/components/trade/compareTypes";

/**
 * One trade-matching panel — "You have, they don't" or "They have, you don't".
 *
 * Both directions are the same surface with the labels and the add-direction
 * swapped, and they were two near-identical 80-line blocks inside Compare. The
 * Trade Hub needs the same pair, so the shape lives here once: a header that
 * doubles as a collapse control, the top-8 grid, and whichever footer the
 * viewer's tier earns (View all for Pro, the unlock tease for Basic).
 *
 * The Pro gate is deliberately asymmetric and must stay that way: for Basic the
 * server already returned only an unfiltered top-N preview, so `rows` (the
 * client-filtered list) is NOT what Basic renders — `previewRows` is. Filtering
 * the full list is the Pro perk; rendering the filtered list to Basic could
 * hide the whole preview behind an active pill.
 */
export function LeadPanel({
  title,
  rows,
  previewRows,
  serverTotal,
  isPro,
  showAll,
  onToggleShowAll,
  onAdd,
  onUnlock,
  emptyCopy,
  testId,
  tileTestIdPrefix,
  className = "mt-4",
}: {
  title: string;
  /** The client-filtered leads. What Pro sees. */
  rows: PanelRow[];
  /** The server's unfiltered top-N preview. What Basic sees. */
  previewRows: PanelRow[];
  /** The true lead count from the server, used for Basic's count + tease. */
  serverTotal: number;
  isPro: boolean;
  showAll: boolean;
  onToggleShowAll: () => void;
  onAdd: (row: PanelRow) => void;
  onUnlock: () => void;
  /** What to say when there is nothing here — the caller knows why. */
  emptyCopy: string;
  testId: string;
  tileTestIdPrefix: string;
  className?: string;
}) {
  const forDisplay = isPro ? rows : previewRows;
  const canCollapse = isPro && rows.length > 8;

  return (
    <section className={className} data-testid={testId}>
      {/* Header doubles as a collapse control: once the list is expanded it can
          be long (thousands of leads), so tapping the header re-collapses it
          without scrolling to the bottom button. */}
      <button
        type="button"
        onClick={() => canCollapse && onToggleShowAll()}
        disabled={!canCollapse}
        aria-expanded={showAll}
        data-testid={`${testId}-collapse`}
        className="w-full flex items-center justify-between mb-3 text-left"
      >
        <h2 className="v2-display-up text-[11px]" style={{ color: "var(--text-ink-bright-faint)" }}>
          {title}
        </h2>
        <span className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono tabular-nums" style={{ color: "var(--text-ink-faint)" }}>
            {isPro ? rows.length : serverTotal}
          </span>
          {canCollapse && (
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`}
              style={{ color: "var(--text-ink-faint)" }}
            />
          )}
        </span>
      </button>

      {forDisplay.length === 0 ? (
        <div className="v2-neon-card-soft p-6 text-center" data-testid={`${testId}-empty`}>
          <Zap className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--text-ink-faint)" }} />
          <p className="text-xs" style={{ color: "var(--text-ink-faint)" }}>
            {emptyCopy}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {(isPro
              ? showAll ? rows : rows.slice(0, 8)
              : forDisplay.slice(0, FREE_COMPARE_LEADS)
            ).map((row, i) => (
              <LeadTile
                key={`${row.variant}-${row.id}-${i}`}
                row={row}
                testId={`${tileTestIdPrefix}${row.id}`}
                onAdd={() => onAdd(row)}
              />
            ))}
          </div>
          {isPro ? (
            <ViewAllButton
              total={rows.length}
              showingAll={showAll}
              onToggle={onToggleShowAll}
              testId={`${testId}-view-all`}
            />
          ) : serverTotal > FREE_COMPARE_LEADS ? (
            <ProTradeTease hidden={serverTotal - FREE_COMPARE_LEADS} onUnlock={onUnlock} />
          ) : null}
        </>
      )}
    </section>
  );
}
